import posthog from 'posthog-js';
import { registerAnalyticsProvider, registerDefaultAnalyticsContext } from './utils/analytics';

let isPostHogInitialized = false;
const MANAGE_USERS_PATH_PREFIX = '/manage-users';
const COMPANY_GROUP_KEY = 'company';

let currentCompanyId: string | null = null;

type PostHogEvent = {
  event?: string;
  properties?: Record<string, unknown>;
};

type EventProperties = Record<string, unknown>;

const getCompanyIdFromProperties = (properties?: EventProperties): string | null => {
  if (!properties) {
    return null;
  }

  const companyIdCandidates = [properties.company_id, properties.companyId, properties.company];

  for (const candidate of companyIdCandidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate;
    }
  }

  return null;
};

const withCompanyGroup = (properties?: EventProperties): EventProperties | undefined => {
  const companyIdFromProperties = getCompanyIdFromProperties(properties);
  const companyId = companyIdFromProperties ?? currentCompanyId;

  if (!companyId) {
    return properties;
  }

  currentCompanyId = companyId;

  const existingGroups =
    properties?.groups && typeof properties.groups === 'object' ? (properties.groups as Record<string, unknown>) : {};

  return {
    ...properties,
    groups: {
      ...existingGroups,
      [COMPANY_GROUP_KEY]: companyId,
    },
  };
};

const isManageUsersPathname = (pathname: string) =>
  pathname === MANAGE_USERS_PATH_PREFIX || pathname.startsWith(`${MANAGE_USERS_PATH_PREFIX}/`);

const getPathnameFromEvent = (event: PostHogEvent): string | null => {
  const currentUrl = event.properties?.$current_url;

  if (typeof currentUrl !== 'string') {
    return null;
  }

  try {
    const baseUrl = globalThis.window?.location.origin ?? 'https://localhost';
    return new URL(currentUrl, baseUrl).pathname;
  } catch {
    return null;
  }
};

const isFuelRouteActive = () => {
  if (globalThis.window === undefined) {
    return false;
  }

  return isManageUsersPathname(globalThis.window.location.pathname);
};

const isLocalAnalyticsLoggingEnabled = () => {
  if (globalThis.window === undefined) {
    return false;
  }

  return globalThis.window.location.hostname === 'localhost';
};

const logPostHogEvent = (
  method: 'capture' | 'identify' | 'screen' | 'captureException' | 'reset',
  payload?: unknown,
) => {
  if (!isLocalAnalyticsLoggingEnabled()) {
    return;
  }

  console.info('[PostHog]', method, payload);
};

const logPostHogCaptureState = () => {
  if (!isLocalAnalyticsLoggingEnabled()) {
    return;
  }

  const internalPostHog = posthog as typeof posthog & {
    __loaded?: boolean;
    persistence?: unknown;
    sessionPersistence?: unknown;
    _requestQueue?: unknown;
    _is_bot?: () => boolean | undefined;
    rateLimiter?: {
      clientRateLimitContext?: () => {
        isRateLimited?: boolean;
        remainingTokens?: number;
      };
    };
    config?: {
      before_send?: unknown;
      __preview_capture_bot_pageviews?: boolean;
    };
  };

  const clientRateLimitContext = internalPostHog.rateLimiter?.clientRateLimitContext?.();
  const isBot = internalPostHog._is_bot?.();

  console.info('[PostHog] state', {
    hasOptedIn: posthog.has_opted_in_capturing(),
    hasOptedOut: posthog.has_opted_out_capturing(),
    isCapturing: posthog.is_capturing(),
    distinctId: posthog.get_distinct_id(),
    isLoaded: internalPostHog.__loaded,
    hasPersistence: Boolean(internalPostHog.persistence),
    hasSessionPersistence: Boolean(internalPostHog.sessionPersistence),
    hasRequestQueue: Boolean(internalPostHog._requestQueue),
    isBot,
    captureBotPageviews: internalPostHog.config?.__preview_capture_bot_pageviews,
    hasBeforeSend: Boolean(internalPostHog.config?.before_send),
    clientRateLimited: clientRateLimitContext?.isRateLimited,
    rateLimitRemainingTokens: clientRateLimitContext?.remainingTokens,
  });
};

export function initPostHog(): void {
  if (isPostHogInitialized) {
    return;
  }

  const isLocal = isLocalAnalyticsLoggingEnabled();
  const { posthogKey, posthogHost } = config;

  posthog.init(posthogKey, {
    api_host: posthogHost,
    person_profiles: 'identified_only',
    opt_out_useragent_filter: isLocal,
    request_batching: !isLocal,
    before_send: (event) => {
      if (!event) {
        return null;
      }

      const eventPathname = getPathnameFromEvent(event);
      const activePathname = globalThis.window?.location.pathname;
      const pathnameToCheck = eventPathname ?? activePathname;

      if (pathnameToCheck && isManageUsersPathname(pathnameToCheck)) {
        return event;
      }

      if (isLocalAnalyticsLoggingEnabled()) {
        console.info('[PostHog] event dropped (not /manage-users)', {
          event: event.event,
          eventPathname,
          activePathname,
        });
      }

      return null;
    },
    on_request_error: (response) => {
      if (!isLocal) {
        return;
      }

      console.error('[PostHog] request failed', response);
    },
  });

  registerAnalyticsProvider({
    capture: (event, properties) => {
      // Only capture events if the current pathname starts with '/manage-users'
      if (!isFuelRouteActive()) {
        if (isLocalAnalyticsLoggingEnabled()) {
          console.info('[PostHog] capture skipped (not /manage-users)', {
            event,
            pathname: globalThis.window?.location.pathname,
          });
        }
        return;
      }
      const captureProperties = withCompanyGroup(properties);

      logPostHogEvent('capture', { event, properties: captureProperties });
      logPostHogCaptureState();

      const captureResult = posthog.capture(event, captureProperties);

      if (isLocalAnalyticsLoggingEnabled()) {
        console.info('[PostHog] capture result', captureResult);
      }
    },
    identify: (distinctId, properties) => {
      logPostHogEvent('identify', { distinctId, properties });
      const companyId = getCompanyIdFromProperties(properties);

      if (companyId) {
        currentCompanyId = companyId;
        posthog.group(COMPANY_GROUP_KEY, companyId);
      }

      posthog.identify(distinctId, properties);
    },
    screen: (screenName, properties) => {
      const screenProperties = withCompanyGroup({
        $screen_name: screenName,
        ...properties,
      });

      logPostHogEvent('screen', { screenName, properties: screenProperties });
      posthog.capture('$screen', {
        ...screenProperties,
      });
    },
    captureException: (error, properties) => {
      const exceptionProperties = withCompanyGroup(properties);

      logPostHogEvent('captureException', {
        error,
        properties: exceptionProperties,
      });
      posthog.captureException(error, exceptionProperties);
    },
    reset: () => {
      logPostHogEvent('reset');
      currentCompanyId = null;
      posthog.reset();
    },
  });
  registerDefaultAnalyticsContext();
  isPostHogInitialized = true;
}

export default posthog;
